import { Injectable, Injector, inject } from '@angular/core';
import { from, of } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { API_CONFIG } from '../config/api.config';
import {
  AUTH_STORAGE_KEY,
  AUTH_SESSION_COOKIE_KEYS,
  getAuthCookie,
  getSharedCookieDomainAttribute,
  persistAuthSession,
  setAuthCookie,
} from '../utils/auth-session.util';
import { SessionLogoutReason } from '../utils/session-auth-error.util';
import { SessionExpiryWarningService } from './session-expiry/session-expiry-warning.service';
import { InactivityService } from './inactivity/inactivity.service';
import { ToastService } from './toast.service';
import { WORKSPACE_CUSTOMER_ID_KEY } from './profile.service';
import { FirebaseNotificationInitService } from './firebase/firebase-notification-init.service';
import { NotificationService } from './notification/notification.service';
import { STORAGE_KEYS } from '../config/storage-keys.config';
import { authFlowDebug } from '../utils/auth-flow-debug.util';
import { HttpService } from './http.service';

export interface AuthUser {
  email: string;
  token?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly sessionExpiryWarning = inject(SessionExpiryWarningService);
  private readonly injector = inject(Injector);
  private readonly toast = inject(ToastService);
  private readonly http = inject(HttpService);
  private loggingOut = false;

  constructor() {
    if (this.getUser()) {
      queueMicrotask(() => this.sessionExpiryWarning.startMonitoring());
    }
  }

  notifySessionLogout(reason: SessionLogoutReason): void {
    authFlowDebug.log('notify_session_logout', 'AuthService.notifySessionLogout', {
      reason,
    });
    const messages: Record<
      SessionLogoutReason,
      { title: string; message: string }
    > = {
      SESSION_VERSION_MISMATCH: {
        title: 'Signed out',
        message: 'Your account has been signed in on another device.',
      },
      TOKEN_EXPIRED: {
        title: 'Session expired',
        message: 'Your session has expired. Please sign in again.',
      },
      INVALID_REFRESH_TOKEN: {
        title: 'Session expired',
        message: 'Your session has expired. Please sign in again.',
      },
      TOKEN_INVALID: {
        title: 'Authentication failed',
        message: 'Authentication failed. Please sign in again.',
      },
    };
    const toast = messages[reason];
    this.toast.warning(toast.title, { message: toast.message });
  }

  private readonly firebaseNotificationInit = inject(
    FirebaseNotificationInitService,
  );
  private readonly notificationService = inject(NotificationService);

  login(email: string, _password: string, rememberMe = false): void {
    const user: AuthUser = { email };
    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
    if (!rememberMe) {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    } else {
      sessionStorage.removeItem(AUTH_STORAGE_KEY);
    }
  }

  /**
   * Revokes the refresh session on the customer logout API, then clears local storage.
   * Always redirects to login, even if the API call fails.
   */
  logout(options?: { source?: string; reason?: string }): void {
    if (this.loggingOut) {
      return;
    }
    this.loggingOut = true;

    const refreshToken =
      (typeof localStorage !== 'undefined'
        ? localStorage.getItem('refreshToken')
        : null) ?? getAuthCookie('refreshToken');
    const fcmToken = this.firebaseNotificationInit.getStoredFcmToken();

    authFlowDebug.log('logout_started', options?.source ?? 'AuthService.logout', {
      reason: options?.reason ?? 'explicit_logout',
      data: {
        logoutUrl: API_CONFIG.auth.logout,
        hasRefreshToken: !!refreshToken,
      },
    });
    this.sessionExpiryWarning.stopMonitoring();
    this.injector.get(InactivityService).stopTracking();
    from(this.firebaseNotificationInit.deactivateCurrentToken())
      .pipe(
        catchError(() => of(null)),
        switchMap(() => {
          if (!refreshToken) {
            return of(null);
          }
          return this.http
            .post(API_CONFIG.auth.logout, {
              refreshToken,
              ...(fcmToken ? { fcmToken } : {}),
            })
            .pipe(catchError(() => of(null)));
        }),
        finalize(() => {
          this.loggingOut = false;
        }),
      )
      .subscribe(() =>
        this.clearSessionAndRedirect(options?.source, options?.reason),
      );
  }

  /** Login auth user id (`localStorage.id`). */
  getAuthUserId(): string | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    return localStorage.getItem(STORAGE_KEYS.AUTH_USER_ID)?.trim() || null;
  }

  private clearSessionAndRedirect(source?: string, reason?: string): void {
    authFlowDebug.persistBeforeSessionClear(
      source ?? 'AuthService.clearSessionAndRedirect',
      reason,
    );
    this.notificationService.clearNotifications();
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(STORAGE_KEYS.FCM_DEVICE_TOKEN);
    }
    localStorage.clear();
    sessionStorage.clear();
    authFlowDebug.restoreAfterSessionClear();
    this.clearAllCookies();
    this.redirectToLogin(source, reason);
  }

  redirectToLogin(source?: string, reason?: string): void {
    if (typeof window === 'undefined') return;
    const targetUrl = (() => {
      try {
        const url = new URL(environment.loginUrl);
        url.searchParams.set('logout', 'true');
        return url.toString();
      } catch {
        return `${environment.loginUrl}?logout=true`;
      }
    })();

    authFlowDebug.log('redirect_to_login', source ?? 'AuthService.redirectToLogin', {
      reason,
      data: {
        targetUrl,
        loginUrl: environment.loginUrl,
      },
    });

    window.location.href = targetUrl;
  }

  private clearAllCookies(): void {
    if (typeof document === 'undefined') return;
    for (const name of AUTH_SESSION_COOKIE_KEYS) {
      this.deleteCookie(name);
    }
  }

  private deleteCookie(name: string): void {
    const domain = getSharedCookieDomainAttribute();
    const secure =
      typeof window !== 'undefined' && window.location.protocol === 'https:'
        ? '; Secure'
        : '';
    const expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = `${name}=;expires=${expires};path=/${domain}; SameSite=Lax${secure}`;
    document.cookie = `${name}=;expires=${expires};path=/; SameSite=Lax${secure}`;
    document.cookie = `${name}=;expires=${expires}; SameSite=Lax${secure}`;
  }

  isLoggedIn(): boolean {
    return !!this.getUser();
  }

  getUser(): AuthUser | null {
    const cookieToken =
      typeof document !== 'undefined' ? this.getCookie(AUTH_STORAGE_KEY) : null;
    const localToken =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem(AUTH_STORAGE_KEY) ||
          localStorage.getItem('accessToken')
        : null;

    const token = cookieToken || localToken;
    if (!token || token.split('.').length !== 3) {
      return null;
    }

    const payload = this.decodeJwt(token);
    let identity = this.readJwtString(
      payload,
      'email',
      'username',
      'sub',
      'phone',
    );
    if (!identity && typeof localStorage !== 'undefined') {
      identity = localStorage.getItem('id')?.trim() || '';
    }
    if (!identity) {
      return null;
    }

    this.cacheCustomerIdFromJwt(payload);
    this.syncAuthStorage(token);

    return { email: identity, token };
  }

  /** Keep localStorage and cookies aligned for cross-subdomain handoff (landing → dashboard). */
  private syncAuthStorage(accessToken: string): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    persistAuthSession({
      accessToken,
      refreshToken:
        this.getCookie('refreshToken') ||
        localStorage.getItem('refreshToken') ||
        undefined,
      workspaceId:
        this.getCookie('workspace_id') ||
        localStorage.getItem('workspace_id') ||
        undefined,
      userId: this.getCookie('id') || localStorage.getItem('id') || undefined,
      role: this.getCookie('role') || localStorage.getItem('role') || undefined,
    });

    const userCookie = this.getCookie('user');
    if (userCookie) {
      localStorage.setItem('user', userCookie);
      setAuthCookie('user', userCookie);
    }
  }

  private getCookie(name: string): string | null {
    if (typeof document === 'undefined') return null;
    const nameLenPlus = name.length + 1;
    return (
      document.cookie
        .split(';')
        .map((c) => c.trim())
        .filter((cookie) => cookie.substring(0, nameLenPlus) === `${name}=`)
        .map((cookie) =>
          decodeURIComponent(cookie.substring(nameLenPlus)),
        )[0] ?? null
    );
  }

  private readJwtString(
    payload: Record<string, unknown> | null,
    ...keys: string[]
  ): string {
    if (!payload) {
      return '';
    }
    for (const key of keys) {
      const value = payload[key];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
    return '';
  }

  private cacheCustomerIdFromJwt(
    payload: Record<string, unknown> | null,
  ): void {
    if (!payload || typeof localStorage === 'undefined') {
      return;
    }
    const customerId =
      (typeof payload['customerId'] === 'string' &&
        payload['customerId'].trim()) ||
      '';
    if (customerId) {
      localStorage.setItem(WORKSPACE_CUSTOMER_ID_KEY, customerId);
    }
    const workspaceId =
      (typeof payload['workspaceId'] === 'string' &&
        payload['workspaceId'].trim()) ||
      (typeof payload['tenant_id'] === 'string' &&
        payload['tenant_id'].trim()) ||
      '';
    if (workspaceId && !localStorage.getItem('workspace_id')) {
      localStorage.setItem('workspace_id', workspaceId);
    }
  }

  private decodeJwt(token: string): Record<string, unknown> | null {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );
      return JSON.parse(jsonPayload) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
}
